import * as Sentry from "@sentry/nextjs";
import { getGraphQLErrors } from "@portal/app/_common/lib/clientErrorUtils";
import { excludeGraphQLFetch } from "apollo-link-sentry";
import { sentryDefaults } from "./sentry";
import { isFacebookAutofillError, isGoogleMapsError } from "@portal/app/_common/lib/sentryFilters";

Sentry.init({
  ...sentryDefaults,
  tunnel: "/api/tunnel",
  // `apollo-link-sentry` tracks GraphQL requests as breadcrumbs.
  // Exclude native fetch breadcrumbs to avoid duplicate GraphQL entries.
  beforeBreadcrumb: excludeGraphQLFetch,
  beforeSend: (event, hint) => {
    if (
      getGraphQLErrors(hint?.originalException).find(
        (gqle) => gqle?.extensions?.shouldLogError === false
      )
    ) {
      return null;
    }
    if (
      ["load failed", "failed to fetch", "AbortError: The user aborted a request."].some(
        (messageToSkip) =>
          (hint?.originalException as Error)?.message
            ?.toLowerCase()
            .includes(messageToSkip.toLowerCase())
      )
    ) {
      return null;
    }
    if (
      event.exception?.values?.some(
        ({ value }) => value && ["NEXT_REDIRECT", "NEXT_NOT_FOUND"].includes(value)
      )
    ) {
      return null;
    }
    // Filter crashes from in-app browsers (TikTok, Instagram) dispatching synthetic
    // touch events with empty touches lists. These are not actionable bugs in our code.
    // isTrusted=false means the event was dispatched programmatically, not by the user.
    const eventArgs = (event.extra as Record<string, unknown>)?.arguments;
    if (
      Array.isArray(eventArgs) &&
      eventArgs[0]?.type === "touchstart" &&
      eventArgs[0]?.isTrusted === false
    ) {
      return null;
    }
    // Filter TypeErrors from Facebook's in-app browser autofill bridge
    // (setContactAutofillValuesFromBridge). These originate entirely in
    // Facebook's injected code and are not actionable. ~300+ events across
    // 4 duplicate Sentry issues.
    if (isFacebookAutofillError(event)) {
      return null;
    }
    // Filter third-party Google Maps errors (e.g. InvalidValueError from maps-api-v3).
    // These originate entirely in Google's JS SDK and are not actionable in our code.
    if (isGoogleMapsError(event)) {
      return null;
    }
    return event;
  },
});

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
